import React, { useState, useContext, useEffect, useCallback } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation
} from 'react-router-dom';
import { AuthProvider, AuthContext } from './AuthContext';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import DesktopMessage from './components/DesktopMessage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import FavouritePage from './pages/FavouritePage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrdersPage from './pages/OrdersPage';
import ProfilePage from './pages/ProfilePage';
import SearchPage from './pages/SearchPage';
import AuthPage from './pages/AuthPage';
import AuthPopup from './components/AuthPopup';

function AppContent({ redirectAfterLogin, setRedirectAfterLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsAuthenticated } = useContext(AuthContext);
  const [authPopupOpen, setAuthPopupOpen] = useState(false);

  // Reset scroll position on route changes
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  // Auth presentation is responsive: on mobile (<768px) login/register is a
  // real page (/login) so it has its own URL, the feed isn't mounted behind
  // it, and the back button works. On desktop it stays the modal, unchanged.
  const handleOpenAuth = useCallback((redirectRoute) => {
    const target = redirectRoute || location.pathname;
    if (window.matchMedia('(max-width: 767px)').matches) {
      navigate('/login', { state: { redirectTo: target } });
    } else {
      setRedirectAfterLogin(target);
      setAuthPopupOpen(true);
    }
  }, [location.pathname, setRedirectAfterLogin, navigate]);

  // Global event listener for openAuthPopup events.
  useEffect(() => {
    const openPopupHandler = (event) => {
      const targetRoute = event.detail && event.detail.redirectRoute ? event.detail.redirectRoute : location.pathname;
      handleOpenAuth(targetRoute);
    };
    window.addEventListener('openAuthPopup', openPopupHandler);
    return () => window.removeEventListener('openAuthPopup', openPopupHandler);
  }, [location, setRedirectAfterLogin, handleOpenAuth]);

  const handleAuthSuccess = () => {
    // Update global auth state
    setIsAuthenticated(true);
    setAuthPopupOpen(false);
    if (redirectAfterLogin && redirectAfterLogin !== location.pathname) {
      navigate(redirectAfterLogin);
    } else {
      navigate("/");
    }
    setRedirectAfterLogin(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
    navigate("/");
  };

  // Set dynamic viewport height for mobile browsers
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // The standalone auth page owns the whole screen - hide the app chrome
  // (header + bottom nav) on those routes.
  const isAuthRoute = location.pathname === '/login' || location.pathname === '/register';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      <DesktopMessage />
      {!isAuthRoute && <Header onOpenAuth={handleOpenAuth} onLogout={handleLogout} />}
      <main style={{ flex: 1 }}>
        <Routes>
          <Route path="/" element={<ProductsPage />} />
          <Route path="/login" element={<AuthPage initialMode="login" />} />
          <Route path="/register" element={<AuthPage initialMode="register" />} />
          <Route path="/favorites" element={<FavouritePage />} />
          <Route path="/product/:id" element={<ProductDetailPage onOpenAuth={handleOpenAuth} />} />
          <Route path="/products/:id" element={<ProductDetailPage onOpenAuth={handleOpenAuth} />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/account" element={<ProfilePage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/:accountName" element={<ProfilePage />} />
        </Routes>
      </main>
      {!isAuthRoute && <BottomNav />}
      <AuthPopup
        isOpen={authPopupOpen}
        onClose={() => setAuthPopupOpen(false)}
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
}

function App() {
  const [redirectAfterLogin, setRedirectAfterLogin] = useState(null);

  return (
    <AuthProvider>
      <Router>
        <AppContent
          redirectAfterLogin={redirectAfterLogin}
          setRedirectAfterLogin={setRedirectAfterLogin}
        />
      </Router>
    </AuthProvider>
  );
}

export default App;
